/**
 * Toasts (prompt.md §37, §49, §78).
 *
 * WHY a provider instead of a per-screen store: the two messages that matter
 * most — "the run stream reconnected" and "an approval is waiting" — can arrive
 * while any screen is open, including in the Tauri shell where a toast is the
 * closest thing to a system notification. One provider, one viewport, one
 * announcement policy (`role="status"` for information, `alert` for failures).
 */

import { createContext, useCallback, useContext, useEffect, useId, useMemo, useRef, useState, type ReactNode } from "react";

import { Z_INDEX } from "@dayvise/design-tokens";

import { cardStyle, rowStyle, stackStyle } from "../styles.ts";
import { toneColor, type StatusTone } from "../logic/runState.ts";
import { IconButton } from "./Button.tsx";

export interface Toast {
  readonly id: string;
  readonly title: string;
  readonly description?: string;
  readonly tone?: StatusTone;
}

export interface ToastInput {
  readonly title: string;
  readonly description?: string;
  readonly tone?: StatusTone;
}

export interface ToastApi {
  readonly toasts: readonly Toast[];
  /** Adds a toast; returns its id so the caller can dismiss it explicitly. */
  notify(input: ToastInput): string;
  dismiss(id: string): void;
  clear(): void;
}

const ToastContext = createContext<ToastApi | null>(null);

const DEFAULT_TTL_MS = 6_000;

export interface ToastProviderProps {
  readonly children: ReactNode;
  /** Milliseconds before a toast removes itself; `null` keeps it until dismissed. */
  readonly ttlMs?: number | null;
  readonly random?: () => number;
}

export function ToastProvider({ children, ttlMs = DEFAULT_TTL_MS, random = Math.random }: ToastProviderProps) {
  const [toasts, setToasts] = useState<readonly Toast[]>([]);
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>());
  const prefix = useId();

  const dismiss = useCallback((id: string) => {
    const timer = timers.current.get(id);
    if (timer !== undefined) {
      clearTimeout(timer);
      timers.current.delete(id);
    }
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const notify = useCallback(
    (input: ToastInput): string => {
      const id = `${prefix}-${Math.round(random() * 1_000_000)}`;
      setToasts((current) => [...current, { id, title: input.title, description: input.description, tone: input.tone ?? "neutral" }]);
      if (ttlMs !== null) timers.current.set(id, setTimeout(() => dismiss(id), ttlMs));
      return id;
    },
    [dismiss, prefix, random, ttlMs],
  );

  useEffect(() => {
    const pending = timers.current;
    return () => {
      for (const timer of pending.values()) clearTimeout(timer);
      pending.clear();
    };
  }, []);

  const api = useMemo<ToastApi>(() => ({ toasts, notify, dismiss, clear: () => setToasts([]) }), [toasts, notify, dismiss]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

/** Access the toast API. Throws when used outside the provider, by design. */
export function useToast(): ToastApi {
  const api = useContext(ToastContext);
  if (!api) throw new Error("useToast must be used inside a ToastProvider");
  return api;
}

export interface ToastViewportProps {
  readonly toasts: readonly Toast[];
  readonly onDismiss: (id: string) => void;
}

export function ToastViewport({ toasts, onDismiss }: ToastViewportProps) {
  if (toasts.length === 0) return null;
  return (
    <div
      aria-live="polite"
      style={{
        position: "fixed",
        right: "var(--dayvise-space-lg)",
        bottom: "var(--dayvise-space-lg)",
        ...stackStyle("var(--dayvise-space-sm)"),
        maxWidth: "360px",
        zIndex: Z_INDEX.toast,
      }}
    >
      {toasts.map((toast) => (
        <ToastCard key={toast.id} toast={toast} onDismiss={() => onDismiss(toast.id)} />
      ))}
    </div>
  );
}

export interface ToastCardProps {
  readonly toast: Toast;
  readonly onDismiss: () => void;
}

export function ToastCard({ toast, onDismiss }: ToastCardProps) {
  const tone = toast.tone ?? "neutral";
  return (
    <div
      role={tone === "danger" ? "alert" : "status"}
      style={{
        ...cardStyle({ elevated: true }),
        padding: "var(--dayvise-space-md)",
        borderLeft: `3px solid ${toneColor(tone)}`,
      }}
    >
      <div style={rowStyle("var(--dayvise-space-sm)", "space-between")}>
        <span style={{ fontSize: "var(--dayvise-font-size-sm)", fontWeight: "var(--dayvise-font-weight-semibold)" }}>{toast.title}</span>
        <IconButton label="Dismiss" size="sm" onClick={onDismiss}>
          <span aria-hidden="true">✕</span>
        </IconButton>
      </div>
      {toast.description === undefined ? null : (
        <p style={{ margin: "var(--dayvise-space-xxs) 0 0", color: "var(--dayvise-color-text-muted)", fontSize: "var(--dayvise-font-size-xs)" }}>{toast.description}</p>
      )}
    </div>
  );
}
